import * as React from "react";
import { useState, useEffect } from "react";
import { Copy, RefreshCw, Check, X, ChevronDown, ChevronRight, Loader2, FileText, Table } from "lucide-react";
import FeedbackButtons from "../atoms/FeedbackButtons";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { isValidOoxml, groupOoxmlChanges } from "../../../../core/lib/diffs";
import type { ChangesStatus } from "../../types/changesTypes";

export interface MessageActionBarProps {
  messageId: string;
  currentFeedback?: 'like' | 'dislike' | null;
  onFeedback: (messageId: string, feedback: 'like' | 'dislike') => void;

  onCopy: () => void | Promise<void>;
  isCopied: boolean;

  onRetry?: () => void;
  showRetry?: boolean;

  hasChanges?: boolean;
  ooxmlChanges?: string; // OOXML string for preview and application
  changesCount?: number;
  changesStatus?: ChangesStatus;
  onUpdateChangesStatus?: (messageId: string, status: ChangesStatus) => Promise<void>;
  onToggleChanges?: () => void;
  isChangesOpen?: boolean;
}

const MessageActionBar: React.FC<MessageActionBarProps> = ({
  messageId,
  currentFeedback,
  onFeedback,
  onCopy,
  isCopied,
  onRetry,
  showRetry = false,
  hasChanges = false,
  ooxmlChanges = '',
  changesCount = 0,
  changesStatus = null,
  onUpdateChangesStatus,
  onToggleChanges,
  isChangesOpen = false
}) => {
  // States for changes
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changesCountState, setChangesCountState] = useState<number | null>(null);
  const [showChangesList, setShowChangesList] = useState(isChangesOpen);

  const changeGroups = React.useMemo(() => {
    if (!ooxmlChanges) return [];
    try {
      return groupOoxmlChanges(ooxmlChanges);
    } catch (error) {
      console.error('Error parsing OOXML changes:', error);
      return [];
    }
  }, [ooxmlChanges]);

  // We assume changes are already automatically applied to the document
  const isApplied = true;
  const isAccepted = changesStatus === 'accepted';
  const isRejected = changesStatus === 'rejected';

  useEffect(() => {
    setShowChangesList(isChangesOpen);
  }, [isChangesOpen]);

  useEffect(() => {
    // If not accepted or rejected (and we assume applied), try to get the changes count
    if (!isAccepted && !isRejected) {
      const updateChangesCount = async () => {
        try {
          const { WordService } = await import('../../../../shared/services/wordService');
          const count = await WordService.getTrackChangesCount();
          setChangesCountState(count);
        } catch (err) {
          console.error('Error getting changes count:', err);
        }
      };
      updateChangesCount();
    }
  }, [isAccepted, isRejected]);

  // Change handlers

  const handleAccept = async (e?: React.MouseEvent) => {
    // Prevenir propagación del evento para evitar que se dispare en otros lugares
    e?.stopPropagation();
    e?.preventDefault();
    
    if (isAccepting || isAccepted) return;
    setIsAccepting(true);
    setError(null);
    try {
      const { WordService } = await import('../../../../shared/services/wordService');
      await WordService.acceptAllTrackChanges();
      setChangesCountState(0);
      if (messageId && onUpdateChangesStatus) {
        await onUpdateChangesStatus(messageId, 'accepted');
      }
    } catch (err: any) {
      console.error('❌ [MessageActionBar] Error accepting changes:', err);
      setError(err.message || 'Error accepting changes');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = async (e?: React.MouseEvent) => {
    // Prevenir propagación del evento para evitar que se dispare en otros lugares
    e?.stopPropagation();
    e?.preventDefault();
    
    if (isRejecting) return;
    setIsRejecting(true);
    setError(null);
    try {
      const { WordService } = await import('../../../../shared/services/wordService');
      await WordService.rejectAllTrackChanges();
      setChangesCountState(0);
      if (messageId && onUpdateChangesStatus) {
        await onUpdateChangesStatus(messageId, 'rejected');
      }
    } catch (err: any) {
      console.error('❌ [MessageActionBar] Error rejecting changes:', err);
      setError(err.message || 'Error rejecting changes');
    } finally {
      setIsRejecting(false);
    }
  };

  const toggleChangesList = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const newValue = !showChangesList;
    setShowChangesList(newValue);
    onToggleChanges?.();
  };

  // Determine what to show
  // Show controls if there are changes and the message is valid
  // Do NOT hide when rejected, only when cancelled
  const showChangesControls = hasChanges && 
    messageId && 
    !messageId.startsWith('temp-') && 
    changesStatus !== 'cancelled';

  const displayChangesCount = changesCountState !== null ? changesCountState : changesCount;

  return (
    <>
      {/* Main action bar */}
      <div className="mt-2.5 flex items-center gap-1.5">
        {/* Left side: Copy, Retry, Feedback */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onCopy}
          className={cn(
            "h-7 w-7 transition-colors hover:bg-transparent",
            isCopied
              ? "text-emerald-600 bg-emerald-50"
              : "text-gray-400 hover:text-gray-600"
          )}
          title={isCopied ? "Copied" : "Copy"}
          aria-label={isCopied ? "Copied" : "Copy"}
        >
          {isCopied ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </Button>

        {showRetry && onRetry && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRetry}
            className="h-7 w-7 text-gray-400 hover:text-gray-600 hover:bg-transparent"
            title="Retry"
            aria-label="Retry"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        )}

        <FeedbackButtons
          messageId={messageId}
          currentFeedback={currentFeedback}
          onFeedback={onFeedback}
          inline
        />

        {/* Spacer */}
        {showChangesControls && <div className="flex-1" />}

        {/* Right side: Changes controls */}
        {showChangesControls && (
          <div 
            className="flex items-center gap-1.5"
            onClick={(e) => {
              // Prevenir propagación de clicks en el contenedor de controles
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              // Prevenir propagación también en mousedown
              e.stopPropagation();
            }}
          >
            {/* Toggle to view changes (only if not accepted or rejected) */}
            {changeGroups.length > 0 && !isAccepted && !isRejected && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleChangesList(e);
                }}
                className={cn(
                  "h-7 px-2 text-[11px] font-medium hover:bg-muted/50 gap-1",
                  "text-muted-foreground",
                  showChangesList && "bg-muted/50"
                )}
              >
                {showChangesList ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {`${displayChangesCount || changeGroups.length} change${(displayChangesCount || changeGroups.length) !== 1 ? 's' : ''}`}
              </Button>
            )}

            {/* Action buttons based on state */}
            {isApplied && !isAccepted && !isRejected && (
              <div 
                className="flex gap-1.5"
                onClick={(e) => {
                  // Prevenir propagación en el contenedor de botones
                  e.stopPropagation();
                }}
              >
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleAccept(e);
                  }}
                  disabled={isAccepting || isRejecting}
                  className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                >
                  {isAccepting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  {isAccepting ? 'Accepting...' : 'Accept'}
                </Button>
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleReject(e);
                  }}
                  disabled={isAccepting || isRejecting}
                  className="h-7 px-2 text-[11px] bg-red-500 hover:bg-red-600 text-white gap-1"
                >
                  {isRejecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                  {isRejecting ? 'Rejecting...' : 'Reject'}
                </Button>
              </div>
            )}

            {/* Status badge when accepted */}
            {isAccepted && (
              <Badge variant="outline" className="text-[10px] h-6 px-2 text-emerald-600 border-emerald-200 bg-emerald-50 gap-1">
                <Check className="h-3 w-3" /> Accepted
              </Badge>
            )}

            {/* Status badge when rejected */}
            {isRejected && (
              <Badge variant="outline" className="text-[10px] h-6 px-2 text-red-600 border-red-200 bg-red-50 gap-1">
                <X className="h-3 w-3" /> Rejected
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Expandable changes list (below the bar) */}
      {showChangesControls && showChangesList && !isAccepted && !isRejected && changeGroups.length > 0 && (
        <div className="mt-2 ml-0 px-3 py-2 bg-slate-50 rounded-lg max-h-[300px] overflow-y-auto shadow-none">
          {changeGroups.map((group, index) => (
            <div
              key={group.id}
              className="mb-2 p-2 rounded-md border border-slate-200 bg-white text-xs"
            >
              <div className="flex justify-between items-center mb-1.5 text-[10px] font-semibold text-muted-foreground uppercase">
                <span>
                  Change {index + 1}
                  {group.type === 'replacement' && ' - Replacement'}
                  {group.type === 'insertion' && ' - Insertion'}
                  {group.type === 'deletion' && ' - Deletion'}
                  {group.type === 'table-insertion' && ' - Table Added'}
                  {group.type === 'table-deletion' && ' - Table Deleted'}
                </span>
              </div>

              {/* Table visualization */}
              {(group.type === 'table-insertion' || group.type === 'table-deletion') && group.tableInfo && (
                <div className={cn(
                  "p-2 rounded-sm border-l-2 text-xs break-words mt-1",
                  group.type === 'table-insertion' 
                    ? "bg-emerald-50/50 border-emerald-500 text-emerald-800" 
                    : "bg-red-50/50 border-red-500 text-red-800"
                )}>
                  <div className="flex items-center gap-1 font-semibold text-[10px] mb-1">
                    <span>{group.type === 'table-insertion' ? '+ TABLE ADDED' : '− TABLE DELETED'}</span>
                    <Table className="h-3 w-3" />
                  </div>
                  <div className="text-[10px] font-medium mb-1.5">
                    {group.tableInfo.summary}
                  </div>
                  
                  {group.tableInfo.data && group.tableInfo.data.length > 0 && (
                    <div className="overflow-x-auto mt-1 bg-white/50 rounded p-1">
                      <table className="w-full border-collapse text-[9px] bg-white">
                        <thead>
                          <tr>
                            {group.tableInfo.data[0].map((header, idx) => (
                              <th key={idx} className={cn(
                                "p-1 text-left font-bold border border-black/10 text-white",
                                group.type === 'table-insertion' ? "bg-emerald-500" : "bg-red-500"
                              )}>
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {group.tableInfo.data.slice(1).map((row, rowIdx) => (
                            <tr key={rowIdx}>
                              {row.map((cell, cellIdx) => (
                                <td key={cellIdx} className={cn(
                                  "p-1 border border-slate-200 text-slate-700",
                                  rowIdx % 2 === 0 ? "bg-white" : "bg-slate-50"
                                )}>
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Deletion visualization */}
              {group.deleted && group.type !== 'table-deletion' && (
                <div className="p-2 bg-red-50/50 border-l-2 border-red-500 rounded-sm mb-1 text-red-800 break-words">
                  <div className="font-semibold text-[10px] mb-0.5">− DELETE</div>
                  <div className="text-[10px]">{group.deleted.text}</div>
                </div>
              )}

              {/* Insertion visualization */}
              {group.inserted && group.type !== 'table-insertion' && (
                <div className="p-2 bg-emerald-50/50 border-l-2 border-emerald-500 rounded-sm text-emerald-800 break-words">
                  <div className="font-semibold text-[10px] mb-0.5">+ ADD</div>
                  <div className="text-[10px]">{group.inserted.text}</div>
                </div>
              )}

              {/* Change author */}
              {(group.deleted?.author || group.inserted?.author) && (
                <div className="mt-1 text-[9px] text-muted-foreground italic">
                  {group.deleted?.author || group.inserted?.author}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="mt-2 px-3 py-2 bg-red-50 text-red-700 text-[10px] rounded-md border border-red-200">
          <strong>Error:</strong> {error}
        </div>
      )}
    </>
  );
};

export default MessageActionBar;
